import { Button, ButtonTheme } from 'components/Button';
import { Card, CardBorderRadius } from 'components/Card';
import { DateField } from 'components/Field/DateField';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Pressable } from 'components/Pressable';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import {
  API_DATE_FORMAT,
  ScreenSize,
  UI_DATE_FORMAT_LONG,
} from 'lib/constants';
import {
  randomKey,
  isoDateStringToDate,
  arrayJoin,
  filterArrayEmpty,
  formatDate,
  dateToIsoString,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import { addDays, format, parseISO, startOfDay } from 'date-fns';
import { utc } from '@date-fns/utc';
import * as React from 'react';
import { View, LayoutChangeEvent } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  ALERT,
  doToastError,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  doEngageScheduledVisitDelete,
  doEngageScheduledVisitSave,
  doEngageScheduledVisitRecipientsLoad,
  doEngageScheduledVisitRecipientsUnload,
  doEngageScheduledVisitTemplatesLoad,
  eventTypeScheduledVisitAdded,
  doEngageScheduledVisitCommunicationScheduleLoad,
  doEngageScheduledVisitCommunicationScheduleUnload,
} from 'store/remote/engageActions';
import {
  EngageScheduledVisitStatus,
  BlockManagerScheduledVisitTimeslot,
  EngageScheduledVisitRecipient,
} from 'types/DTOs/BlockManagerDTOs';
import {
  EngageInspector,
  EngageScheduledVisitListItem,
  SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS,
} from 'types/Engage';
import { ISODateString } from 'types/types';
import { useScreenSize } from 'hooks/useScreenSize';
import { EngageScheduledVisitInspectors } from './Components/EngageScheduledVisitInspectors';
import { EngageScheduledVisitTimeline } from './Components/EngageScheduledVisitTimeline';
import { createId } from '@paralleldrive/cuid2';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import {
  selectEngageScheduledVisitRecipientsData,
  selectEngageScheduledVisitRecipientsLoading,
  selectEngagePropertiesItem,
  selectEngageScheduledVisitCommunicationScheduleData,
  selectEngageScheduledVisitCommunicationScheduleLoading,
  selectEngageScheduledVisitTemplatesData,
  selectEngageScheduledVisitTemplatesLoading,
} from 'store/remote/engageSelectors';
import { pluraliseS } from 'lib/pluralise';
import { CardRow } from 'components/CardRow';
import { chunk, noop } from 'lodash';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { State } from 'store/types';
import {
  DeliveryGroup,
  EngageCommunicationDeliveryGroups,
} from './Components/EngageCommunicationDeliveryGroups';
import { doAddEvent, doRemoveEvent } from 'lib/events';

type ScheduleDate = {
  id: string;
  dateString: ISODateString;
  hours: number[];
};

// const HOURS = Array.from({ length: 24 }, (_, index) => index);
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const HOURS_AM = HOURS.filter((h) => h < 13);
const HOURS_PM = HOURS.filter((h) => h >= 13);

// Build the groups the same way that EngageSendCommunicationModal's
// exisitng logic does
const buildDeliveryGroupsFromRecipients = (
  flats: EngageScheduledVisitRecipient[] | undefined,
): DeliveryGroup[] => {
  const groups: Record<'letter' | 'email', DeliveryGroup> = {
    letter: { label: '', key: 'letter', flats: [] },
    email: { label: '', key: 'email', flats: [] },
  };
  (flats ?? []).forEach((flat) => {
    groups[
      flat.deliverabilityStatus === 'error' ? 'letter' : 'email'
    ].flats.push({
      id: flat.id,
      name: flat.name,
      text: getDeliverabilityStatusReasonDescription(
        flat.deliverabilityStatusReason,
      ),
    });
  });
  groups.letter.label = `${pluraliseS(groups.letter.flats, 'flat')} will receive a letter`;
  groups.email.label = `${pluraliseS(groups.email.flats, 'flat')} will receive an email`;
  return [
    groups.letter.flats.length ? groups.letter : null,
    groups.email.flats.length ? groups.email : null,
  ].filter(filterArrayEmpty);
};

const getDeliverabilityStatusReasonDescription = (
  deliverabilityStatusReason: EngageScheduledVisitRecipient['deliverabilityStatusReason'],
): string => {
  switch (deliverabilityStatusReason) {
    case 'no-contacts':
      return 'No contacts available';
    case 'no-residents':
      return 'No residents - will send letter and email owners';
    case 'no-contact-emails':
      return 'No contact emails';
    case 'no-resident-emails':
      return 'No resident emails - emailing owners and sending letters';
    case 'no-primary-residents':
      return 'No primary residents - emailing all residents';
    case 'primary-residents':
    default:
      return '';
  }
};

type SetHourSelection = (
  dateId: string,
  hour: number,
  shouldSelect: boolean | null,
) => void;
type HandleStartDrag = (
  dateId: string,
  hour: number,
  isSelected: boolean,
) => void;
type HandleEnterDuringDrag = (dateId: string, hour: number) => void;
type HandleEndDrag = () => void;

const buildScheduleDatesFromSlots = (
  slots: EngageScheduledVisitListItem['slots'],
): ScheduleDate[] =>
  slots.map((slot) => ({
    id: createId(),
    dateString: slot.date,
    hours: [...slot.hours],
  }));

export const EngageFlatDoorScheduledVisitModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlatDoorScheduledVisit>
> = (props) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const { propertyId, pageKey, scheduledVisit } = props.route.params;

  const [scheduledVisitId, setScheduledVisitId] = React.useState<
    string | undefined
  >(scheduledVisit?.id);

  const isPublished =
    scheduledVisit?.status === EngageScheduledVisitStatus.PUBLISHED;
  const isCancelled =
    scheduledVisit?.status === EngageScheduledVisitStatus.CANCELLED;
  const isHistoric = !!scheduledVisit?.historic;
  const templates = useSelector((state: State) =>
    selectEngageScheduledVisitTemplatesData(state, pageKey),
  );
  const templateCommunicationSchedule = useSelector((state: State) =>
    selectEngageScheduledVisitCommunicationScheduleData(state, pageKey),
  );
  const templatesStatus = useSelector((state: State) =>
    selectEngageScheduledVisitTemplatesLoading(state, pageKey),
  );
  const communicationScheduleStatus = useSelector((state: State) =>
    selectEngageScheduledVisitCommunicationScheduleLoading(state, pageKey),
  );
  /**
   * pendingDate is automatically set to lastDate+1day when scheduledDates
   * is updated. Unless pendingDateIsCustomisedRef=true as the user has
   * explicitly selected it
   */
  const [pendingDate, setPendingDate] = React.useState(
    format(
      scheduledVisit?.endDateTime
        ? addDays(isoDateStringToDate(scheduledVisit?.endDateTime), 1)
        : addDays(new Date(), SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS),
      API_DATE_FORMAT,
    ),
  );

  // Specific callback for date field to mark pendingDate as customised
  const setPendingDateFromDateField = React.useCallback((value: string) => {
    pendingDateIsCustomisedRef.current = true;
    setPendingDate(value);
  }, []);

  const [copiedHours, setCopiedHours] = React.useState<number[]>([]);
  const [copyId, setCopyId] = React.useState<string | null>(null);
  const [scheduleDates, setScheduleDates] = React.useState<ScheduleDate[]>(
    scheduledVisit ? buildScheduleDatesFromSlots(scheduledVisit.slots) : [],
  );
  const isDraggingRef = React.useRef(false);
  const dragTargetRef = React.useRef<boolean | null>(null);
  const today = React.useMemo(() => startOfDay(new Date(), { in: utc }), []);
  const [inspectors, setInspectors] = React.useState<EngageInspector[]>(
    scheduledVisit?.inspectors ?? [],
  );
  const hasFutureDates = scheduleDates.some(
    (item) => startOfDay(parseISO(item.dateString, { in: utc })) >= today,
  );
  const hasPastDates = scheduleDates.some(
    (item) => startOfDay(parseISO(item.dateString, { in: utc })) < today,
  );
  const endDateHasPassed =
    !!scheduledVisit?.endDateTime &&
    startOfDay(isoDateStringToDate(scheduledVisit.endDateTime)) < today;

  // View only can accomodate other variables in the future
  const isViewOnly = isPublished || isCancelled;

  const canSaveDraft = !isPublished && !isCancelled;
  const canSavePublished = isPublished;
  const canSaveInspectors =
    canSaveDraft || (canSavePublished && !endDateHasPassed);
  const canCancel = isPublished && hasFutureDates;
  const canPublish = !isPublished && !isCancelled;

  const pendingDateIsCustomisedRef = React.useRef(false);

  const recipients = useSelector((state) =>
    selectEngageScheduledVisitRecipientsData(state, pageKey),
  );
  const recipientsLoading = useSelector((state) =>
    selectEngageScheduledVisitRecipientsLoading(state, pageKey),
  );
  const property = useSelector((state) =>
    selectEngagePropertiesItem(state, pageKey, propertyId),
  );
  const deliveryGroups = React.useMemo(
    () => buildDeliveryGroupsFromRecipients(recipients?.flats),
    [recipients?.flats],
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);
  React.useEffect(() => {
    if (templatesStatus === 'notloaded') {
      dispatch(
        doEngageScheduledVisitTemplatesLoad({
          key: pageKey,
        }),
      );
    }
  }, [dispatch, pageKey, templatesStatus]);
  React.useEffect(() => {
    if (!scheduledVisitId) {
      return;
    }

    dispatch(
      doEngageScheduledVisitCommunicationScheduleLoad({
        key: pageKey,
        scheduledVisitId,
        propertyUid: propertyId,
      }),
    );
    return () => {
      dispatch(
        doEngageScheduledVisitCommunicationScheduleUnload({
          key: pageKey,
        }),
      );
    };
  }, [dispatch, pageKey, scheduledVisitId, propertyId]);

  // Effect to update id to info handled in modal
  React.useEffect(() => {
    const event = doAddEvent(eventTypeScheduledVisitAdded, (action) => {
      setScheduledVisitId(action.payload.scheduledVisitId);
    });
    return () => {
      doRemoveEvent(event);
    };
  }, []);

  React.useEffect(() => {
    if (isHistoric) {
      return;
    }

    dispatch(
      doEngageScheduledVisitRecipientsLoad({
        key: pageKey,
        propertyId,
      }),
    );
    return () => {
      dispatch(
        doEngageScheduledVisitRecipientsUnload({
          key: pageKey,
        }),
      );
    };
  }, [dispatch, pageKey, propertyId, isHistoric]);

  React.useEffect(() => {
    if (pendingDateIsCustomisedRef.current) {
      return;
    }
    const newPendingDate =
      scheduleDates.reduce<Date | null>((acc, scheduleDate) => {
        const thisDate = isoDateStringToDate(scheduleDate.dateString);
        if (!acc || thisDate >= acc) {
          return addDays(thisDate, 1);
        }
        return acc;
      }, null) ||
      addDays(new Date(), SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS);
    setPendingDate(format(newPendingDate, API_DATE_FORMAT));
  }, [scheduleDates]);

  const handleAddDate = React.useCallback(
    (newDateString: string) => {
      const newIsoDateString = dateToIsoString(
        parseISO(newDateString, { in: utc }),
      );
      // Error if this date has already be added
      const matchedDate = scheduleDates.some(
        (item) => item.dateString === newIsoDateString,
      );
      if (matchedDate) {
        dispatch(doToastError('This date is already selected'));
        return;
      }

      // The user has added this date so pendingDate becomes non-customised
      pendingDateIsCustomisedRef.current = false;

      // Add new date
      setScheduleDates((current) => [
        ...current,
        {
          id: randomKey(),
          dateString: newIsoDateString,
          hours: [],
        },
      ]);
    },
    [scheduleDates, dispatch],
  );

  const handleUpdateDate = React.useCallback(
    (index: number, newDateString: string) => {
      if (!newDateString) {
        return;
      }
      const newIsoDateString = dateToIsoString(
        parseISO(newDateString, { in: utc }),
      );
      // Error if this date has already be added
      const matchedDate = scheduleDates.some(
        (sd, i) => i !== index && sd.dateString === newIsoDateString,
      );
      if (matchedDate) {
        dispatch(doToastError('This date is already selected'));
        return;
      }
      // Update existing date
      setScheduleDates(
        scheduleDates.map((sd, i) =>
          i !== index ? sd : { ...sd, dateString: newIsoDateString },
        ),
      );
    },
    [scheduleDates, dispatch],
  );

  const handleCopy = React.useCallback(
    (index: number) => {
      const schedule = scheduleDates[index];
      if (!schedule) {
        return;
      }
      setCopiedHours(schedule.hours);
      setCopyId(schedule.id);
    },
    [scheduleDates],
  );

  const handleCancelCopy = React.useCallback(() => {
    setCopiedHours([]);
    setCopyId(null);
  }, []);

  const handlePaste = React.useCallback(
    (index: number) => {
      setScheduleDates((current) =>
        current.map((sd, i) =>
          i !== index
            ? sd
            : {
                ...sd,
                hours: [...copiedHours],
              },
        ),
      );
    },
    [copiedHours],
  );

  const setHourSelection = React.useCallback<SetHourSelection>(
    (dateId, hour, shouldSelect) => {
      setCopyId(null);
      setScheduleDates((current) =>
        current.map((item) => {
          if (item.id !== dateId) {
            return item;
          }
          const isSelected = item.hours.includes(hour);
          const nextState =
            shouldSelect === null ? !isSelected : shouldSelect === true;
          const updatedHours =
            nextState === isSelected
              ? item.hours
              : nextState
                ? [...item.hours, hour].sort((a, b) => a - b)
                : item.hours.filter((value) => value !== hour);
          return {
            ...item,
            hours: updatedHours,
          };
        }),
      );
    },
    [],
  );

  const handleStartDrag = React.useCallback<HandleStartDrag>(
    (dateId, hour, isSelected) => {
      const target = !isSelected;
      dragTargetRef.current = target;
      isDraggingRef.current = true;
      setHourSelection(dateId, hour, target);
    },
    [setHourSelection],
  );

  const handleEnterDuringDrag = React.useCallback<HandleEnterDuringDrag>(
    (dateId, hour) => {
      if (!isDraggingRef.current || dragTargetRef.current === null) {
        return;
      }
      setHourSelection(dateId, hour, dragTargetRef.current);
    },
    [setHourSelection],
  );

  const handleEndDrag = React.useCallback<HandleEndDrag>(() => {
    isDraggingRef.current = false;
    dragTargetRef.current = null;
  }, []);

  const handleCancel = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageCancelScheduledVisit,
        params: {
          propertyId: propertyId,
          pageKey: pageKey,
          scheduledVisitId: scheduledVisit!.id,
        },
      }),
    );
  }, [dispatch, pageKey, propertyId, scheduledVisit]);

  const getValidationMessage = React.useCallback(
    (status: EngageScheduledVisitStatus) => {
      const hasDateWithoutHours = scheduleDates.some(
        (item) => item.hours.length === 0,
      );

      if (
        hasPastDates &&
        status === EngageScheduledVisitStatus.PUBLISHED &&
        scheduledVisit?.status === EngageScheduledVisitStatus.PUBLISHED
      ) {
        return {
          title: 'Save is disabled for past dates',
          text: 'This visit is already saved. The Save button is disabled because one or more dates are in the past. Only future dates can be updated.',
          hasErrors: true,
        };
      }

      // Validate before save
      const errors: string[] = [];
      const solutions: string[] = []; // add message for action(s)
      if (!scheduleDates.length) {
        errors.push('at least one date is required');
        solutions.push('add a date to the schedule timeline');
      } else if (hasDateWithoutHours) {
        errors.push('all dates must have at least one hour selected');
        solutions.push(
          'remove unused dates or select at least one hour for each date',
        );
      }
      if (hasPastDates && status === EngageScheduledVisitStatus.PUBLISHED) {
        errors.push('all dates must be in the future');
        solutions.push('remove or change past dates');
      }

      if (errors.length) {
        const verb = !scheduledVisit
          ? 'Save'
          : status === EngageScheduledVisitStatus.PUBLISHED &&
              scheduledVisit.status !== EngageScheduledVisitStatus.PUBLISHED
            ? 'Publish'
            : 'Save';
        const verbPastParticiple = !scheduledVisit
          ? 'created'
          : status === EngageScheduledVisitStatus.PUBLISHED &&
              scheduledVisit?.status !== EngageScheduledVisitStatus.PUBLISHED
            ? 'published'
            : 'updated';
        return {
          title: `Cannot ${verb}`,
          text: `This scheduled visit cannot be ${verbPastParticiple} because ${arrayJoin(errors, ', ', ' and ')}. To publish this visit, ${arrayJoin(solutions, ', ', ' and ')}.`,
          hasErrors: true,
        };
      }

      return {
        title: '',
        text: '',
        hasErrors: false,
      };
    },
    [hasPastDates, scheduledVisit, scheduleDates],
  );
  const handleSave = React.useCallback(
    (status: EngageScheduledVisitStatus) => {
      // Prepare timeslots for API
      const timeslots = scheduleDates.map<BlockManagerScheduledVisitTimeslot>(
        (item) => ({
          date: item.dateString,
          hours: item.hours,
        }),
      );

      const { hasErrors, title, text } = getValidationMessage(status);

      if (hasErrors) {
        dispatch(
          ALERT({
            title,
            text,
          }),
        );
        return;
      }

      dispatch(
        doEngageScheduledVisitSave({
          propertyId,
          pageKey,
          timeslots,
          scheduledVisitId: scheduledVisitId,
          status,
          inspectorIds: inspectors.map((inspector) => inspector.id),
          currentStatus:
            scheduledVisit?.status ?? EngageScheduledVisitStatus.DRAFT,
        }),
      );
    },
    [
      scheduleDates,
      dispatch,
      propertyId,
      pageKey,
      scheduledVisit,
      inspectors,
      getValidationMessage,
      scheduledVisitId,
    ],
  );

  const disablePublishedSaveButton = canSavePublished && hasPastDates;
  const inspectionTeamEmptyStateText = canSaveInspectors
    ? 'No inspectors assigned yet.'
    : 'No inspectors assigned for this visit.';

  const handleRemoveDate = React.useCallback(
    (dateId: string) => {
      if (dateId === copyId) {
        setCopyId(null);
      }
      setScheduleDates((current) =>
        current.filter((item) => item.id !== dateId),
      );
    },
    [copyId],
  );

  const handleDeleteDraft = () =>
    scheduledVisitId
      ? dispatch(
          doEngageScheduledVisitDelete({
            propertyId,
            pageKey,
            scheduledVisitId,
          }),
        )
      : noop;

  const earliestSelectedDate = React.useMemo(() => {
    return scheduleDates.reduce<Date | null>((earliest, scheduleDate) => {
      const currentDate = parseISO(scheduleDate.dateString, { in: utc });
      if (!earliest || currentDate < earliest) {
        return currentDate;
      }
      return earliest;
    }, null);
  }, [scheduleDates]);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={'Scheduled Visit'}
          subheading={
            screenSize === ScreenSize.small
              ? 'FED Check'
              : 'Flat Entrance Door Check'
          }
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          buttons={
            canSaveDraft || canSavePublished || canCancel || canPublish ? (
              <View
                style={{
                  flexDirection: 'row',
                  columnGap: theme.paddingHorizontal,
                  alignItems: 'center',
                }}
              >
                {canSaveDraft && (
                  <Button
                    text="Save Draft"
                    buttonTheme={ButtonTheme.secondary}
                    onPress={() => handleSave(EngageScheduledVisitStatus.DRAFT)}
                  />
                )}
                {canSavePublished && (
                  <Button
                    text="Save"
                    buttonTheme={ButtonTheme.secondary}
                    disabled={disablePublishedSaveButton}
                    onPress={() =>
                      handleSave(EngageScheduledVisitStatus.PUBLISHED)
                    }
                  />
                )}
                {canPublish && (
                  <Button
                    text="Publish"
                    buttonTheme={ButtonTheme.primary}
                    onPress={() =>
                      handleSave(EngageScheduledVisitStatus.PUBLISHED)
                    }
                  />
                )}
                {canCancel && (
                  <Button
                    text="Cancel"
                    buttonTheme={ButtonTheme.primary}
                    styleHovered={{ backgroundColor: theme.colors.red80 }}
                    stylePressed={{ backgroundColor: theme.colors.red80 }}
                    textStyle={{ color: theme.colors.white }}
                    style={{
                      backgroundColor: theme.colors.red,
                      borderColor: theme.colors.red,
                    }}
                    onPress={handleCancel}
                  />
                )}
              </View>
            ) : undefined
          }
        />
      }
    >
      {scheduledVisit?.status === EngageScheduledVisitStatus.CANCELLED ? (
        <Card>
          <CardRow
            label={`Cancelled${
              scheduledVisit?.cancelledDate
                ? ` on ${formatDate(
                    isoDateStringToDate(scheduledVisit.cancelledDate),
                    UI_DATE_FORMAT_LONG,
                  )}`
                : ''
            }`}
            labelColor={theme.colors.red}
            labelAlignTop
            value="This visit‘s details can no longer be updated."
          />
          {!!scheduledVisit?.cancelledReason && (
            <CardRow
              label="Reason for cancellation"
              labelAlignTop
              value={scheduledVisit.cancelledReason}
            />
          )}
        </Card>
      ) : (
        scheduledVisit?.status === EngageScheduledVisitStatus.PUBLISHED && (
          <Card>
            <CardRow
              label={`Published  ${
                scheduledVisit?.publishedDate
                  ? `on ${formatDate(
                      isoDateStringToDate(scheduledVisit?.publishedDate),
                      UI_DATE_FORMAT_LONG,
                    )}`
                  : ''
              }`}
              labelColor={theme.colors.green}
              labelAlignTop
              value={
                canSaveInspectors
                  ? 'This visit‘s dates can no longer be changed. The inspection team can still be updated.'
                  : 'This visit can no longer be updated as all scheduled dates have passed.'
              }
            />
          </Card>
        )
      )}
      {isHistoric && !isPublished && !isCancelled && (
        <Card>
          <CardRow
            label="Historic"
            labelColor={theme.colors.orange}
            labelAlignTop
            value="This scheduled visit has passed."
          />
        </Card>
      )}
      {canPublish &&
        getValidationMessage(EngageScheduledVisitStatus.PUBLISHED)
          .hasErrors && (
          <Card>
            <CardRow
              label={
                getValidationMessage(EngageScheduledVisitStatus.PUBLISHED).title
              }
              labelColor={theme.colors.red}
              labelAlignTop
              value={
                getValidationMessage(EngageScheduledVisitStatus.PUBLISHED).text
              }
            />
          </Card>
        )}
      <Card label="Schedule Timeline" labelSize="large" margin={false}></Card>
      <View style={{ gap: theme.paddingHorizontalHalf }}>
        {scheduleDates.map((item, index) => (
          <Card key={item.id} withShadow={false} margin={false}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: theme.paddingHorizontal,
                backgroundColor: theme.colors.white,
                gap: theme.paddingHorizontal,
                pointerEvents: isViewOnly ? 'none' : 'auto',
              }}
            >
              {isViewOnly ? (
                <Txt style={{ flex: 1 }}>
                  {format(item.dateString, UI_DATE_FORMAT_LONG)}
                </Txt>
              ) : (
                <DateField
                  value={format(item.dateString, API_DATE_FORMAT)}
                  onChange={(value) => handleUpdateDate(index, value)}
                  placeholder="Select a date"
                  min={
                    // This stops the date resetting on field blur if opening
                    // an existing visit with dates older than today
                    isoDateStringToDate(item.dateString) < today
                      ? isoDateStringToDate(item.dateString)
                      : today
                  }
                  clearable
                  showFieldTypeIcon={false}
                  showClearButton={false}
                  shrinkToWidth
                  style={{
                    minHeight: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    backgroundColor: 'transparent',
                    borderRadius: 8,
                    flex: 1,
                  }}
                />
              )}
              {!isViewOnly && (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: theme.paddingHorizontal / 2,
                  }}
                >
                  {scheduleDates.length > 1 &&
                    (!copyId || copyId !== item.id) && (
                      <Button
                        buttonTheme={ButtonTheme.link}
                        onPress={() => handleCopy(index)}
                        text="Copy"
                        chipLike
                      />
                    )}
                  {scheduleDates.length > 1 &&
                    !!copyId &&
                    (copyId !== item.id ? (
                      <Button
                        buttonTheme={ButtonTheme.link}
                        onPress={() => handlePaste(index)}
                        text="Paste"
                        chipLike
                      />
                    ) : (
                      <Button
                        buttonTheme={ButtonTheme.secondary}
                        onPress={handleCancelCopy}
                        text="Cancel Copy"
                        destructive
                        chipLike
                      />
                    ))}
                  <IconButton
                    icon={ICONS.TrashCanLight}
                    onPress={() => handleRemoveDate(item.id)}
                  />
                </View>
              )}
            </View>
            <ViewWithSeparators
              horizontal
              style={{
                backgroundColor: theme.colors.white80,
                gap: theme.paddingHorizontal / 4,
                flexDirection: 'row',
              }}
            >
              <HourGroup
                scheduleDate={item}
                key="morning"
                title="Morning"
                hours={HOURS_AM}
                isViewOnly={isViewOnly}
                setHourSelection={setHourSelection}
                handleEndDrag={handleEndDrag}
                handleEnterDuringDrag={handleEnterDuringDrag}
                handleStartDrag={handleStartDrag}
              />
              <HourGroup
                scheduleDate={item}
                key="morning"
                title="Morning"
                hours={HOURS_PM}
                isViewOnly={isViewOnly}
                setHourSelection={setHourSelection}
                handleEndDrag={handleEndDrag}
                handleEnterDuringDrag={handleEnterDuringDrag}
                handleStartDrag={handleStartDrag}
              />
            </ViewWithSeparators>
          </Card>
        ))}
        {!isViewOnly && (
          <Card margin={false}>
            <CardRow
              contentStyle={{
                paddingTop: theme.paddingHorizontalHalf,
                paddingBottom: theme.paddingHorizontalHalf,
              }}
            >
              <ViewWithSeparators
                horizontal
                separatorMargin={theme.paddingHorizontalHalf}
                separatorColor="transparent"
                style={{ alignItems: 'center', justifyContent: 'flex-start' }}
              >
                <DateField
                  value={pendingDate}
                  onChange={setPendingDateFromDateField}
                  clearable={false}
                  min={today}
                  showFieldTypeIcon={false}
                  shrinkToWidth
                  style={{
                    paddingLeft: 0,
                    paddingRight: 0,
                    minHeight: 0,
                  }}
                />
                <Button
                  text={'Add Date'}
                  icon={ICONS.PlusLight}
                  buttonTheme={ButtonTheme.primary}
                  onPress={() => handleAddDate(pendingDate)}
                />
              </ViewWithSeparators>
            </CardRow>
          </Card>
        )}
      </View>
      <View style={{ height: theme.paddingVertical }}></View>
      <EngageScheduledVisitInspectors
        propertyId={propertyId}
        inspectors={inspectors}
        onInspectorsChanged={setInspectors}
        canEdit={canSaveInspectors}
        emptyStateText={inspectionTeamEmptyStateText}
      />

      <SeparatorHorizontal style={{ marginVertical: theme.paddingVertical }} />

      <EngageScheduledVisitTimeline
        templates={templates}
        communicationSchedule={templateCommunicationSchedule}
        scheduledVisitStatus={scheduledVisit?.status}
        earliestSelectedDate={earliestSelectedDate}
        publishedDate={isoDateStringToDate(
          scheduledVisit?.publishedDate || null,
        )}
        loading={
          templatesStatus !== 'loaded' ||
          (!!scheduledVisitId && communicationScheduleStatus !== 'loaded')
        }
      />

      {!isHistoric && (
        <>
          <SeparatorHorizontal
            style={{ marginVertical: theme.paddingVertical }}
          />
          <EngageCommunicationDeliveryGroups
            property={property!}
            deliveryGroups={deliveryGroups}
            loading={recipientsLoading}
          />
        </>
      )}

      <View style={{ height: 50 }}></View>
      {scheduledVisit?.status === EngageScheduledVisitStatus.DRAFT && (
        <View>
          <SeparatorHorizontal withMarginBottom />
          <Button
            text="Delete Draft"
            destructive
            buttonTheme={ButtonTheme.themeless}
            onPress={handleDeleteDraft}
            style={{
              alignSelf: 'center',
              marginBottom: theme.paddingVertical,
            }}
          />
        </View>
      )}
    </PageScrollView>
  );
};

const HourGroup = React.memo(
  ({
    scheduleDate,
    title,
    hours,
    isViewOnly,
    handleEnterDuringDrag,
    handleStartDrag,
    setHourSelection,
    handleEndDrag,
  }: {
    scheduleDate: ScheduleDate;
    title: string;
    hours: number[];
    isViewOnly: boolean;
    setHourSelection: SetHourSelection;
    handleStartDrag: HandleStartDrag;
    handleEnterDuringDrag: HandleEnterDuringDrag;
    handleEndDrag: HandleEndDrag;
  }) => {
    const [rows, setRows] = React.useState(2);
    const handleLayout = (event: LayoutChangeEvent) => {
      setRows(event.nativeEvent.layout.width < 350 ? 2 : 1);
    };

    return (
      <View
        key={`${scheduleDate.id}-${title}`}
        style={{
          flex: 1,
          padding: theme.paddingHorizontal,
        }}
        onLayout={handleLayout}
      >
        <View style={{ gap: theme.paddingHorizontal / 4 }}>
          {chunk(hours, hours.length / rows).map((hoursChunk) => (
            <View
              key={hoursChunk.join('-')}
              style={{
                flexDirection: 'row',
                gap: theme.paddingHorizontal / 4,
              }}
            >
              {hoursChunk.map((hour) => (
                <HourBlock
                  key={hour}
                  hour={hour}
                  dateId={scheduleDate.id}
                  isViewOnly={isViewOnly}
                  isSelected={scheduleDate.hours.includes(hour)}
                  handleEnterDuringDrag={handleEnterDuringDrag}
                  handleStartDrag={handleStartDrag}
                  setHourSelection={setHourSelection}
                  handleEndDrag={handleEndDrag}
                />
              ))}
            </View>
          ))}
        </View>
      </View>
    );
  },
);

const HourBlock = React.memo(
  ({
    dateId,
    hour,
    isSelected,
    isViewOnly,
    handleEnterDuringDrag,
    handleStartDrag,
    setHourSelection,
    handleEndDrag,
  }: {
    hour: number;
    dateId: string;
    isSelected: boolean;
    isViewOnly: boolean;
    setHourSelection: SetHourSelection;
    handleStartDrag: HandleStartDrag;
    handleEnterDuringDrag: HandleEnterDuringDrag;
    handleEndDrag: HandleEndDrag;
  }) => {
    const interactionProps = isViewOnly
      ? {}
      : {
          onPress: () => setHourSelection(dateId, hour, isSelected),
          onPressIn: () => handleStartDrag(dateId, hour, isSelected),
          onPressOut: handleEndDrag,
          onMoveShouldSetResponder: () => true,
          onStartShouldSetResponder: () => true,
          onResponderTerminationRequest: () => false,
          onResponderGrant: () => handleEnterDuringDrag(dateId, hour),
          onResponderMove: () => handleEnterDuringDrag(dateId, hour),
          onResponderRelease: handleEndDrag,
          onResponderTerminate: handleEndDrag,
          onMouseEnter: () => handleEnterDuringDrag(dateId, hour),
        };
    const AMPM = hour < 12 ? 'am' : 'pm';
    return (
      <Pressable
        key={hour}
        disabled={isViewOnly}
        pointerEvents={isViewOnly ? 'none' : undefined}
        {...interactionProps}
        style={{
          flex: 1,
          height: 50,
          borderRadius: CardBorderRadius,
          borderWidth: 1,
          borderColor: isSelected ? theme.colors.green : theme.colors.black20,
          backgroundColor: isSelected ? theme.colors.green : theme.colors.white,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: !isSelected && isViewOnly ? 0.42 : 1,
        }}
        hoveredStyle={{
          borderColor: isSelected ? theme.colors.green80 : theme.colors.green10,
          backgroundColor: isSelected
            ? theme.colors.green80
            : theme.colors.green10,
        }}
        pressedStyle={{
          borderColor: isSelected ? theme.colors.green80 : theme.colors.green20,
          backgroundColor: isSelected
            ? theme.colors.green80
            : theme.colors.green20,
        }}
      >
        <Txt
          weight="medium"
          style={{
            color: isSelected ? 'white' : theme.colors.text,
          }}
          selectable={false}
          numberOfLines={1}
        >
          {hour === 0 ? '12' : hour < 13 ? hour : hour - 12}
          <Txt
            size="xsmall"
            weight="medium"
            style={{
              color: isSelected ? 'white' : theme.colors.text,
              opacity: 0.4,
            }}
            selectable={false}
          >
            {' '}
            {AMPM}
          </Txt>
        </Txt>
      </Pressable>
    );
  },
);
